import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import StatusBadge, { PriorityBadge, AcknowledgmentBadge } from '../components/ui/StatusBadge';
import TabsComponent from '../components/ui/TabsComponent';
import { db, COLLECTIONS } from '../firebase/config';
import { deleteDoc, doc } from 'firebase/firestore';
import { 
  CheckSquare,
  Clock,
  AlertTriangle,
  XCircle,
  CheckCircle,
  Calendar,
  User,
  MessageSquare,
  Paperclip,
  Edit,
  Trash2,
  ChevronLeft,
  Plus,
  FileText,
  Activity,
  Info,
  Search,
  BarChart3,
  FolderOpen,
  Building2,
  Globe,
  X,
  Users,
  List
} from 'lucide-react';

const Tasks = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, hasPermission } = useAuth();
  const { 
    tasks, 
    users, 
    projects,
    departments,
    teams,
    updateTask, 
    acknowledgeTask,
    createTask,
    createNotification,
    createComment,
    restoreTask,
    permanentDeleteTask
  } = useData();
  
  const [selectedTask, setSelectedTask] = useState(null);
  const [showEditTask, setShowEditTask] = useState(false);
  const [editTaskForm, setEditTaskForm] = useState({
    title: '',
    description: '',
    assigneeId: '',
    assigneeIds: [],
    teamMembers: [],
    priority: 'important_not_urgent',
    dueDate: '',
    dueTime: '',
    estimate: 0,
    status: 'ממתין לעבודה',
    projectId: ''
  });
  const [editAssigneeDeptFilter, setEditAssigneeDeptFilter] = useState('');
  const [showNewTask, setShowNewTask] = useState(false);
  const [comment, setComment] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') || 'details';
  });
  const [mainTab, setMainTab] = useState(
    currentUser?.role === 'team_manager' ? 'team' : 
    currentUser?.role === 'employee' ? 'personal' : 'management'
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCompleted, setFilterCompleted] = useState(false);
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterMyTasks, setFilterMyTasks] = useState('my'); // ברירת מחדל: המשימות שלי
  const [filterOverdue, setFilterOverdue] = useState(false); // פילטר משימות באיחור
  const [statusTab, setStatusTab] = useState('ממתין לעבודה'); // טאב הסטטוס הנבחר

  // Refs לסגירת הטופס בלחיצה חיצונית
  const modalRef = useRef(null);

  // קריאת פרמטרים מה-URL והגדרת פילטרים
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    if (params.get('filterCompleted') === 'true') {
      setFilterCompleted(true);
    }
    
    if (params.get('filterStatus')) {
      setFilterStatus(params.get('filterStatus'));
    }
    
    if (params.get('filterOverdue') === 'true') {
      setFilterOverdue(true);
    }
  }, [location.search]);

  // State חדשים להערכת זמן משופרת
  const [estimateDays, setEstimateDays] = useState(0);
  const [estimateHours, setEstimateHours] = useState(0);
  const [estimateMinutes, setEstimateMinutes] = useState(0);

  // State לבחירת תאריך ושעה משופרת
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);

  // New task form state
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    projectId: '',
    assigneeId: '',
    assigneeIds: [],
    teamMembers: [],
    departmentId: '',
    teamId: '',
    scope: '', // התחלה ריקה כדי לאלץ בחירה
    dueDate: '',
    dueTime: '09:00',
    estimate: 0,
    priority: 'important_not_urgent',
    subtasks: []
  });

  const [assigneeDeptFilter, setAssigneeDeptFilter] = useState('');
  const [newSubtask, setNewSubtask] = useState('');
  const [editSubtask, setEditSubtask] = useState('');

  // Helper function to check if task should be hidden from current user
  const shouldHideTask = (task) => {
    const taskProject = task.projectId ? projects.find(p => p.id === task.projectId) : null;
    
    const hidePersonalTask = task.scope === 'personal' && task.assigneeId !== currentUser.id && task.createdBy !== currentUser.id;
    const hidePersonalProjectTask = taskProject && taskProject.scope === 'personal' && taskProject.assigneeId !== currentUser.id && taskProject.createdBy !== currentUser.id;
    
    return hidePersonalTask || hidePersonalProjectTask;
  };
  
  // פונקציה חדשה ליצירת התראות למנהלים על משימות אישיות
  const notifyManagersAboutPersonalTask = async (taskData, taskId) => {
    const taskUser = users.find(u => u.id === taskData.assigneeId);
    if (!taskUser) return;
    
    const managersToNotify = users.filter(user => {
      if (user.id === taskUser.id) return false;
      
      switch(taskUser.role) {
        case 'employee':
          return user.teamId === taskUser.teamId && user.role === 'team_manager' ||
                 user.departmentId === taskUser.departmentId && user.role === 'dept_manager' ||
                 ['deputy_ceo', 'ceo', 'org_admin'].includes(user.role);
                 
        case 'team_manager':
          return user.departmentId === taskUser.departmentId && user.role === 'dept_manager' ||
                 ['deputy_ceo', 'ceo', 'org_admin'].includes(user.role);
                 
        case 'dept_manager':
          return ['deputy_ceo', 'ceo', 'org_admin'].includes(user.role);
          
        case 'deputy_ceo':
          return ['ceo', 'org_admin'].includes(user.role);
          
        default:
          return false;
      }
    });
    
    for (const manager of managersToNotify) {
      await createNotification({
        userId: manager.id,
        type: 'personal_task_created',
        title: 'משימה אישית חדשה',
        message: `${taskUser.name} קבע/ה משימה אישית: ${taskData.title}`,
        taskId: taskId,
        createdAt: new Date(),
        isRead: false,
        link: '/team-availability'
      });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && ['details', 'comments', 'activity', 'files'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  useEffect(() => {
    if (id) {
      const task = tasks.find(t => t.id === id);
      setSelectedTask(task || null);
    } else {
      setSelectedTask(null);
    }
  }, [id, tasks]);

  useEffect(() => {
    if (!showNewTask && selectedTask) return;
    if (!currentUser?.email || !Array.isArray(users) || users.length === 0) return;
    const me = users.find(u => u.email === currentUser.email);
    if (me && newTask.assigneeId !== me.id) {
      setNewTask(prev => ({ 
        ...prev, 
        assigneeId: me.id, 
        assigneeIds: prev.assigneeIds?.length ? prev.assigneeIds : [me.id],
        teamMembers: prev.teamMembers?.length ? prev.teamMembers : [me.id]
      }));
    }
  }, [users, currentUser?.email, showNewTask, selectedTask]);

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    await updateTask(selectedTask.id, { status: newStatus });
  };

  // Confirm status change with note
  const confirmStatusChange = async () => {
    if (!statusNote.trim()) {
      alert('נא להזין הערה');
      return;
    }

    await updateTask(selectedTask.id, { 
      status: pendingStatus,
      statusNote: statusNote,
      statusUpdatedAt: new Date()
    });

    setShowStatusModal(false);
    setStatusNote('');
    setPendingStatus('');
  };

  // Handle acknowledgment
  const handleAcknowledge = async () => {
    await acknowledgeTask(selectedTask.id);
  };

  // חישוב הערכת זמן כוללת
  const calculateTotalEstimate = () => {
    return (estimateDays * 8) + estimateHours + (estimateMinutes / 60);
  };

  // Handle create task
  const handleCreateTask = async (e) => {
    e.preventDefault();
    
    // בדיקה ששדה התחום נבחר
    if (!newTask.scope) {
      alert('נא לבחור תחום משימה (אישי/ניהולי)');
      return;
    }
    
    try {
      const currentUserDoc = users.find(u => u.email === currentUser.email);
      const assigneeUserDoc = users.find(u => u.id === newTask.assigneeId);

      // חישוב הערכת זמן כוללת
      const totalEstimate = calculateTotalEstimate();

      const taskData = {
        ...newTask,
        createdBy: currentUserDoc?.id || currentUser.id,
        creatorId: currentUserDoc?.id || currentUser.id,
        assigneeId: assigneeUserDoc?.id || newTask.assigneeId || currentUserDoc?.id,
        assigneeEmail: assigneeUserDoc?.email || currentUser.email,
        scope: newTask.scope, // השתמש בבחירה של המשתמש
        assigneeIds: newTask.scope === 'managerial' ? 
          [...new Set([...newTask.assigneeIds, ...newTask.teamMembers])] : 
          [assigneeUserDoc?.id || currentUserDoc?.id],
        estimate: totalEstimate,
        estimateDetails: {
          days: estimateDays,
          hours: estimateHours,
          minutes: estimateMinutes
        },
        isNew: true,
        acknowledged: false
      };
      
      // למשימות אישיות, נסיר את המחלקה והצוות
      if (taskData.scope === 'personal') {
        delete taskData.departmentId;
        delete taskData.teamId;
      }

      // יצירת המשימה
      const taskId = await createTask(taskData);

      // אם זו משימה אישית - יצירת התראות למנהלים
      if (taskData.scope === 'personal') {
        await notifyManagersAboutPersonalTask(taskData, taskId);
      }

      // יצירת התראות למוקצים
      for (const assigneeId of taskData.assigneeIds) {
        if (assigneeId !== currentUser.id) {
          const assignee = users.find(u => u.id === assigneeId);
          await createNotification({
            userId: assigneeId,
            type: 'task_assigned',
            title: 'משימה חדשה הוקצתה לך',
            message: `המשימה "${taskData.title}" הוקצתה לך`,
            taskId: taskId,
            createdAt: new Date(),
            isRead: false,
            isNew: true,
            link: `/tasks/${taskId}`
          });
        }
      }

      // התראות למחלקה/צוות אם רלוונטי
      if (taskData.departmentId && taskData.scope === 'managerial') {
        const deptUsers = users.filter(u => u.departmentId === taskData.departmentId);
        for (const user of deptUsers) {
          if (!taskData.assigneeIds.includes(user.id)) {
            await createNotification({
              userId: user.id,
              type: 'department_task',
              title: 'משימה חדשה במחלקה',
              message: `משימה חדשה "${taskData.title}" נוספה למחלקה שלך`,
              taskId: taskId,
              createdAt: new Date(),
              isRead: false,
              isNew: true,
              link: `/tasks/${taskId}`
            });
          }
        }
      }

      // עדכון הטאב הרלוונטי אחרי יצירת המשימה
      if (taskData.scope === 'personal') {
        setMainTab('personal');
      } else {
        setMainTab('management');
      }

      // איפוס הטופס
      setShowNewTask(false);
      setNewTask({
        title: '',
        description: '',
        projectId: '',
        assigneeId: '',
        assigneeIds: [],
        teamMembers: [],
        departmentId: '',
        teamId: '',
        scope: '',
        dueDate: '',
        dueTime: '09:00',
        estimate: 0,
        priority: 'important_not_urgent',
        subtasks: []
      });
      setEstimateDays(0);
      setEstimateHours(0);
      setEstimateMinutes(0);
      setAssigneeDeptFilter('');
      setEditAssigneeDeptFilter('');
      
    } catch (error) {
      console.error('Error creating task:', error);
      alert('שגיאה ביצירת המשימה');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName === 'TEXTAREA') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Handle delete task
  const handleDeleteTask = async (task) => {
    try {
      await deleteDoc(doc(db, COLLECTIONS.TASKS, task.id));
      console.log('Task deleted successfully');
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('שגיאה במחיקת המשימה');
    }
  };

  // Can edit task
  const canEditTask = (task) => {
    if (!task || !currentUser) return false;
    
    if (task.createdBy === currentUser.id || task.creatorId === currentUser.id) return true;
    if (task.assigneeId === currentUser.id && task.scope === 'personal') return true;
    if (currentUser.role === 'dept_manager' && task.departmentId === currentUser.departmentId) return true;
    if (currentUser.role === 'ceo' || currentUser.role === 'deputy_ceo') return true;
    if (currentUser.role === 'org_admin') return true;
    
    return false;
  };

  // Tasks list view
  if (!selectedTask) {
    const filteredTasks = tasks.filter(task => {
      if (task.isDailyRoutine || task.routineId) {
        return false;
      }
      
      // Handle deleted tasks tab
      if (mainTab === 'deleted') {
        // Only show deleted tasks in deleted tab
        if (!task.deleted) return false;
        
        // Check if task is older than 14 days
        if (task.deletedAt) {
          const deletedDate = task.deletedAt?.toDate ? task.deletedAt.toDate() : new Date(task.deletedAt);
          const daysSinceDeleted = (new Date() - deletedDate) / (1000 * 60 * 60 * 24);
          if (daysSinceDeleted > 14) return false; // Don't show tasks older than 14 days
        }
      } else {
        // Hide deleted tasks from all other tabs
        if (task.deleted) return false;
      }
      
      let hasAccess = false;
      
   if (currentUser?.role === 'employee') {
  if (mainTab === 'management') {
    hasAccess = false;
    
    // Check if user is directly assigned to the task AND it's managerial
    if (task.assigneeIds && task.assigneeIds.includes(currentUser.id) && task.scope === 'managerial') {
      hasAccess = true;
    }
          
          if (!hasAccess && task.teamId) {
            const myTeams = teams.filter(t => {
              const user = users.find(u => u.id === currentUser.id);
              if (user?.teamIds && Array.isArray(user.teamIds)) {
                return user.teamIds.includes(t.id);
              }
              if (user?.teamId) {
                return user.teamId === t.id;
              }
              return false;
            });
            hasAccess = myTeams.some(team => team.id === task.teamId);
          }
          
          if (!hasAccess && task.departmentId && currentUser.departmentId) {
            hasAccess = task.departmentId === currentUser.departmentId;
          }
          
          hasAccess = hasAccess && task.scope === 'managerial';
        } else {
          const meUser = users.find(u => u.email === currentUser.email);
          const myIds = [currentUser.id, meUser?.id].filter(Boolean);

          hasAccess = (task.scope === 'personal' && 
                      (task.assigneeId === currentUser.id || 
                       task.assigneeId === meUser?.id ||
                       task.createdBy === currentUser.id ||
                       task.createdBy === meUser?.id ||
                       task.creatorId === currentUser.id ||
                       task.creatorId === meUser?.id));
        }
      } else if (currentUser?.role === 'dept_manager') {
        if (mainTab === 'management') {
          // Check if user is directly assigned to the task
          if (task.assigneeIds && task.assigneeIds.includes(currentUser.id) && task.scope === 'managerial') {
            hasAccess = true;
          }
          
          if (!hasAccess) {
            hasAccess = task.departmentId === currentUser.departmentId ||
                       (task.teamId && teams.some(team => team.id === task.teamId && team.departmentId === currentUser.departmentId));
          }
        } else {
          const meUser = users.find(u => u.email === currentUser.email);
          const myIds = [currentUser.id, meUser?.id].filter(Boolean);

          hasAccess = (task.scope === 'personal' && 
                      (task.assigneeId === currentUser.id || 
                       task.assigneeId === meUser?.id ||
                       task.createdBy === currentUser.id ||
                       task.createdBy === meUser?.id ||
                       task.creatorId === currentUser.id ||
                       task.creatorId === meUser?.id));
        }
      } else if (currentUser?.role === 'team_manager') {
        if (mainTab === 'team') {
          // Check if user is directly assigned to the task
          if (task.assigneeIds && task.assigneeIds.includes(currentUser.id) && task.scope === 'managerial') {
            hasAccess = true;
          }
          
          if (!hasAccess) {
            const userTeam = teams.find(team => team.managerId === currentUser.id);
            hasAccess = userTeam && (
              task.teamId === userTeam.id ||
              (task.scope === 'managerial' && task.teamId === userTeam.id)
            );
          }
        } else {
          const meUser = users.find(u => u.email === currentUser.email);
          const myIds = [currentUser.id, meUser?.id].filter(Boolean);

          hasAccess = (task.scope === 'personal' && 
                      (task.assigneeId === currentUser.id || 
                       task.assigneeId === meUser?.id ||
                       task.createdBy === currentUser.id ||
                       task.createdBy === meUser?.id ||
                       task.creatorId === currentUser.id ||
                       task.creatorId === meUser?.id));
        }
      } else if (currentUser?.role === 'org_admin' || currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo') {
        if (mainTab === 'management') {
          // Check if user is directly assigned to the task
          if (task.assigneeIds && task.assigneeIds.includes(currentUser.id)&& task.scope === 'managerial') {
            hasAccess = true;
          }
          
          if (!hasAccess) {
            hasAccess = (task.scope === 'managerial' || task.scope === 'department' || 
                        (task.scope === 'managerial' && task.teamId));
          }
        } else {
          const meUser = users.find(u => u.email === currentUser.email);
          hasAccess = (task.scope === 'personal' && 
                      (task.createdBy === currentUser.id || 
                       task.createdBy === meUser?.id ||
                       task.assigneeId === currentUser.id ||
                       task.assigneeId === meUser?.id ||
                       task.creatorId === currentUser.id ||
                       task.creatorId === meUser?.id));
        }
      }
      
      if (!hasAccess) return false;
      if (shouldHideTask(task)) return false;
      
      const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           task.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = filterStatus === 'all' || 
                           task.status === filterStatus ||
                           (filterStatus === 'במסלול' && task.status === 'בתהליך') ||
                           (filterStatus === 'בתהליך' && task.status === 'במסלול');
      
      const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
      
      let matchesCompleted = true;
      if (filterCompleted) {
        matchesCompleted = task.status === 'הושלם';
      } else {
        matchesCompleted = true;
      }
      
      const matchesAssignee = filterAssignee === 'all' || task.assigneeId === filterAssignee;
      const matchesDepartment = filterDepartment === 'all' || task.departmentId === filterDepartment;
      const matchesProject = filterProject === 'all' || task.projectId === filterProject;
      const matchesTeam = filterTeam === 'all' || task.teamId === filterTeam;
      
      // פילטר "המשימות שלי" - רק משימות שאני משויך אליהן (לא משימות שרק יצרתי)
      const meUser = users.find(u => u.email === currentUser?.email);
      const matchesMyTasks = filterMyTasks === 'all' || 
        task.assigneeId === currentUser?.id || 
        task.assigneeId === meUser?.id ||
        (task.assigneeIds && (task.assigneeIds.includes(currentUser?.id) || task.assigneeIds.includes(meUser?.id)));

      // פילטר משימות באיחור
      let matchesOverdue = true;
      if (filterOverdue) {
        if (task.dueDate) {
          const dueDate = task.dueDate?.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          dueDate.setHours(0, 0, 0, 0);
          matchesOverdue = dueDate < today && task.status !== 'הושלם';
        } else {
          matchesOverdue = false;
        }
      }

      return matchesSearch && matchesStatus && matchesPriority && matchesCompleted && 
             matchesAssignee && matchesDepartment && matchesProject && matchesTeam && matchesMyTasks && matchesOverdue;
    });

    return (
      <div className="p-4 md:p-6">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center">
            <h1 className="text-xl md:text-2xl font-bold">משימות</h1>
            <button
              onClick={() => setShowNewTask(true)}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 min-h-[44px] touch-manipulation"
            >
              <Plus size={20} />
              משימה חדשה
            </button>
          </div>
        </div>
{/* Main Tabs - עיצוב מחודש */}
{(currentUser?.role === 'org_admin' || currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo' || currentUser?.role === 'employee' || currentUser?.role === 'dept_manager') && (
  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl border-2 border-gray-200 mb-6 overflow-hidden">
    <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-2">
      <div className="h-1 bg-white opacity-30 rounded-full"></div>
    </div>
    <div className="border-b border-gray-200">
      <nav className="flex flex-wrap gap-2 px-6">
        <button
          onClick={() => setMainTab('management')}
          className={`relative py-4 px-6 font-semibold text-sm transition-all duration-300 ${
            mainTab === 'management'
              ? 'text-purple-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
              mainTab === 'management' 
                ? 'bg-gradient-to-br from-purple-500 to-pink-600 text-white shadow-lg' 
                : 'bg-gray-100 text-gray-500'
            }`}>
              <Building2 size={20} />
            </div>
            <span>ארגוני</span>
          </div>
          {mainTab === 'management' && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-600 rounded-t-full"></div>
          )}
        </button>
        <button
          onClick={() => setMainTab('personal')}
          className={`relative py-4 px-6 font-semibold text-sm transition-all duration-300 ${
            mainTab === 'personal'
              ? 'text-purple-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
              mainTab === 'personal' 
                ? 'bg-gradient-to-br from-purple-500 to-pink-600 text-white shadow-lg' 
                : 'bg-gray-100 text-gray-500'
            }`}>
              <User size={20} />
            </div>
            <span>אישי</span>
          </div>
          {mainTab === 'personal' && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-600 rounded-t-full"></div>
          )}
        </button>
        <button
          onClick={() => setMainTab('deleted')}
          className={`relative py-4 px-6 font-semibold text-sm transition-all duration-300 ${
            mainTab === 'deleted'
              ? 'text-red-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
              mainTab === 'deleted' 
                ? 'bg-gradient-to-br from-red-500 to-orange-600 text-white shadow-lg' 
                : 'bg-gray-100 text-gray-500'
            }`}>
              <Trash2 size={20} />
            </div>
            <span>נמחקו</span>
          </div>
          {mainTab === 'deleted' && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-orange-600 rounded-t-full"></div>
          )}
        </button>
      </nav>
    </div>
    <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50">
      <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
        <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
        {mainTab === 'management' ? (
          <span>צפייה במשימות ארגוניות</span>
        ) : mainTab === 'deleted' ? (
          <span>משימות שנמחקו - יישמרו ל-14 יום</span>
        ) : (
          <span>צפייה במשימות אישיות</span>
        )}
      </div>
    </div>
  </div>
)}

{/* Team Manager Tabs - עיצוב מחודש */}
{currentUser?.role === 'team_manager' && (
  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl border-2 border-gray-200 mb-6 overflow-hidden">
    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-2">
      <div className="h-1 bg-white opacity-30 rounded-full"></div>
    </div>
    <div className="border-b border-gray-200">
      <nav className="flex space-x-8 px-6">
        <button
          onClick={() => setMainTab('team')}
          className={`relative py-4 px-6 font-semibold text-sm transition-all duration-300 ${
            mainTab === 'team'
              ? 'text-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
              mainTab === 'team' 
                ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg' 
                : 'bg-gray-100 text-gray-500'
            }`}>
              <Users size={20} />
            </div>
            <span>צוותי</span>
          </div>
          {mainTab === 'team' && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-full"></div>
          )}
        </button>
        <button
          onClick={() => setMainTab('personal')}
          className={`relative py-4 px-6 font-semibold text-sm transition-all duration-300 ${
            mainTab === 'personal'
              ? 'text-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
              mainTab === 'personal' 
                ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg' 
                : 'bg-gray-100 text-gray-500'
            }`}>
              <User size={20} />
            </div>
            <span>אישי</span>
          </div>
          {mainTab === 'personal' && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-full"></div>
          )}
        </button>
        <button
          onClick={() => setMainTab('deleted')}
          className={`relative py-4 px-6 font-semibold text-sm transition-all duration-300 ${
            mainTab === 'deleted'
              ? 'text-red-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
              mainTab === 'deleted' 
                ? 'bg-gradient-to-br from-red-500 to-orange-600 text-white shadow-lg' 
                : 'bg-gray-100 text-gray-500'
            }`}>
              <Trash2 size={20} />
            </div>
            <span>נמחקו</span>
          </div>
          {mainTab === 'deleted' && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-orange-600 rounded-t-full"></div>
          )}
        </button>
      </nav>
    </div>
    <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50">
      <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
        <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
        {mainTab === 'team' ? (
          <span>צפייה במשימות הצוות</span>
        ) : mainTab === 'deleted' ? (
          <span>משימות שנמחקו - יישמרו ל-14 יום</span>
        ) : (
          <span>צפייה במשימות אישיות</span>
        )}
      </div>
    </div>
  </div>
)}  

        {/* Team Manager Tabs */}
        {currentUser?.role === 'team_manager' && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setMainTab('team')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    mainTab === 'team'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Users size={20} />
                    צוותי
                  </div>
                </button>
                <button
                  onClick={() => setMainTab('personal')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    mainTab === 'personal'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <User size={20} />
                    אישי
                  </div>
                </button>
              </nav>
            </div>
          </div>
        )}

{/* Filters - עיצוב מחודש */}
<div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl border-2 border-gray-200 p-6 mb-6">
  <div className="flex items-center gap-2 mb-4">
    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
      <Search className="w-5 h-5 text-white" />
    </div>
    <h3 className="text-lg font-bold text-gray-800">סינון וחיפוש משימות</h3>
  </div>
  
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4">
    {/* Search */}
    <div className="sm:col-span-2 lg:col-span-3 xl:col-span-2">
      <div className="relative">
        <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="חיפוש משימות..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pr-10 pl-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all min-h-[44px] bg-white shadow-sm hover:shadow-md"
        />
      </div>
    </div>

    {/* My Tasks Filter - NEW! */}
    <div className="relative">
      <select 
        value={filterMyTasks}
        onChange={(e) => setFilterMyTasks(e.target.value)}
        className="w-full px-4 py-3 border-2 border-purple-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all min-h-[44px] bg-gradient-to-r from-purple-50 to-pink-50 shadow-sm hover:shadow-md appearance-none cursor-pointer font-bold"
      >
        <option value="all">🌐 כל המשימות</option>
        <option value="my">👤 המשימות שלי</option>
      </select>
      <ChevronLeft className="absolute left-3 top-1/2 transform -translate-y-1/2 rotate-90 text-purple-400 pointer-events-none" size={20} />
    </div>

    {/* Status Filter */}
    <div className="relative">
      <select 
        value={filterStatus}
        onChange={(e) => setFilterStatus(e.target.value)}
        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all min-h-[44px] bg-white shadow-sm hover:shadow-md appearance-none cursor-pointer font-medium"
      >
        <option value="all">📊 כל הסטטוסים</option>
        <option value="ממתין לאישור">⏰ ממתין לאישור</option>
        <option value="ממתין לעבודה">⏳ ממתין לעבודה</option>
        <option value="במסלול">🚀 במסלול</option>
        <option value="הושלם">✅ הושלם</option>
      </select>
      <ChevronLeft className="absolute left-3 top-1/2 transform -translate-y-1/2 rotate-90 text-gray-400 pointer-events-none" size={20} />
    </div>

    {/* Priority Filter */}
    <div className="relative">
      <select 
        value={filterPriority}
        onChange={(e) => setFilterPriority(e.target.value)}
        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all min-h-[44px] bg-white shadow-sm hover:shadow-md appearance-none cursor-pointer font-medium"
      >
        <option value="all">⚡ כל העדיפויות</option>
        <option value="important_urgent">🔥 חשוב ודחוף</option>
        <option value="important_not_urgent">⭐ חשוב ולא דחוף</option>
        <option value="not_important_urgent">⚡ לא חשוב ודחוף</option>
        <option value="not_important_not_urgent">📝 לא חשוב ולא דחוף</option>
      </select>
      <ChevronLeft className="absolute left-3 top-1/2 transform -translate-y-1/2 rotate-90 text-gray-400 pointer-events-none" size={20} />
    </div>

    {/* Assignee Filter */}
    <div className="relative">
      <select 
        value={filterAssignee}
        onChange={(e) => setFilterAssignee(e.target.value)}
        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all min-h-[44px] bg-white shadow-sm hover:shadow-md appearance-none cursor-pointer font-medium"
      >
        <option value="all">👥 כל המוקצים</option>
        {users.map(user => (
          <option key={user.id} value={user.id}>{user.name}</option>
        ))}
      </select>
      <ChevronLeft className="absolute left-3 top-1/2 transform -translate-y-1/2 rotate-90 text-gray-400 pointer-events-none" size={20} />
    </div>

    {/* Department Filter */}
    <div className="relative">
      <select 
        value={filterDepartment}
        onChange={(e) => setFilterDepartment(e.target.value)}
        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all min-h-[44px] bg-white shadow-sm hover:shadow-md appearance-none cursor-pointer font-medium"
      >
        <option value="all">🏢 כל המחלקות</option>
        {departments.map(dept => (
          <option key={dept.id} value={dept.id}>{dept.name}</option>
        ))}
      </select>
      <ChevronLeft className="absolute left-3 top-1/2 transform -translate-y-1/2 rotate-90 text-gray-400 pointer-events-none" size={20} />
    </div>

    {/* Project Filter */}
    <div className="relative">
      <select 
        value={filterProject}
        onChange={(e) => setFilterProject(e.target.value)}
        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all min-h-[44px] bg-white shadow-sm hover:shadow-md appearance-none cursor-pointer font-medium"
      >
        <option value="all">📁 כל הפרויקטים</option>
        {projects.map(project => (
          <option key={project.id} value={project.id}>{project.name}</option>
        ))}
      </select>
      <ChevronLeft className="absolute left-3 top-1/2 transform -translate-y-1/2 rotate-90 text-gray-400 pointer-events-none" size={20} />
    </div>

    {/* Team Filter */}
    {(currentUser?.role === 'ceo' || currentUser?.role === 'deputy_ceo' || currentUser?.role === 'org_admin') && (
      <div className="relative">
        <select 
          value={filterTeam}
          onChange={(e) => setFilterTeam(e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all min-h-[44px] bg-white shadow-sm hover:shadow-md appearance-none cursor-pointer font-medium"
        >
          <option value="all">👥 כל הצוותים</option>
          {teams.map(team => (
            <option key={team.id} value={team.id}>{team.name}</option>
          ))}
        </select>
        <ChevronLeft className="absolute left-3 top-1/2 transform -translate-y-1/2 rotate-90 text-gray-400 pointer-events-none" size={20} />
      </div>
    )}

    {/* Completed Filter Button */}
    <button 
      onClick={() => setFilterCompleted(!filterCompleted)}
      className={`px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 min-h-[44px] w-full shadow-lg hover:shadow-xl transform hover:scale-105 ${
        filterCompleted 
          ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' 
          : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-300'
      }`}
    >
      {filterCompleted ? '✅ משימות שהושלמו' : '📋 הצג הכל'}
    </button>
  </div>
</div>

      {/* Tasks Grid - עיצוב מחודש */}
{filterCompleted && (
  <div className="mb-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border-2 border-green-200">
    <h2 className="text-xl font-bold text-green-700 mb-2 flex items-center gap-2">
      <CheckCircle size={24} />
      משימות שהושלמו
    </h2>
    <p className="text-sm text-green-600">משימות שהושלמו בהצלחה</p>
  </div>
)}

{/* טאבים לפי סטטוס */}
{mainTab !== 'deleted' && filteredTasks.length > 0 && (
  <div className="mb-6">
    <div className="flex gap-2 bg-white p-2 rounded-xl shadow-md">
      <button
        onClick={() => setStatusTab('ממתין לעבודה')}
        className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
          statusTab === 'ממתין לעבודה'
            ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
      >
        <Clock className="w-5 h-5" />
        <span>ממתין לעבודה</span>
        <span className="bg-white/20 px-2 py-1 rounded-full text-sm">
          {filteredTasks.filter(t => t.status === 'ממתין לעבודה').length}
        </span>
      </button>
      
      <button
        onClick={() => setStatusTab('בתהליך')}
        className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
          statusTab === 'בתהליך'
            ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
      >
        <Activity className="w-5 h-5" />
        <span>בתהליך</span>
        <span className="bg-white/20 px-2 py-1 rounded-full text-sm">
          {filteredTasks.filter(t => t.status === 'בתהליך' || t.status === 'במסלול').length}
        </span>
      </button>
      
      <button
        onClick={() => setStatusTab('הושלם')}
        className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
          statusTab === 'הושלם'
            ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
      >
        <CheckCircle className="w-5 h-5" />
        <span>הושלם</span>
        <span className="bg-white/20 px-2 py-1 rounded-full text-sm">
          {filteredTasks.filter(t => t.status === 'הושלם').length}
        </span>
      </button>
    </div>
  </div>
)}

<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
  {filteredTasks.length === 0 && mainTab === 'deleted' ? (
    <div className="col-span-full bg-white rounded-2xl shadow-lg p-16 text-center border-2 border-gray-200">
      <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-full w-32 h-32 mx-auto mb-6 flex items-center justify-center">
        <CheckCircle className="w-20 h-20 text-green-500" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-3">אין משימות שנמחקו</h3>
      <p className="text-gray-600 text-lg mb-8 max-w-md mx-auto">
        כל המשימות נמצאות במקומן הנכון. משימות שנמחקות יופיעו כאן למשך 14 יום.
      </p>
      <div className="p-4 bg-blue-50 rounded-lg border-r-4 border-blue-500 max-w-md mx-auto">
        <p className="text-blue-900 text-sm">
          <strong>💡 טיפ:</strong> כשתמחק משימה, היא תישמר כאן ל-14 יום ותוכל לשחזר אותה בכל עת.
        </p>
      </div>
    </div>
  ) : filteredTasks.length === 0 ? (
    <div className="col-span-full bg-white rounded-2xl shadow-lg p-16 text-center border-2 border-gray-200">
      <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-full w-32 h-32 mx-auto mb-6 flex items-center justify-center">
        <Info className="w-20 h-20 text-purple-500" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-3">אין משימות</h3>
      <p className="text-gray-600 text-lg mb-8 max-w-md mx-auto">
        {searchTerm || filterStatus !== 'all' || filterPriority !== 'all' || filterAssignee !== 'all'
          ? 'לא נמצאו משימות התואמות את הסינון שבחרת'
          : 'טרם נוצרו משימות במערכת. התחל ביצירת המשימה הראשונה!'}
      </p>
    </div>
  ) : filteredTasks
    .filter(task => {
      // סינון לפי טאב סטטוס (רק אם זה לא טאב deleted)
      if (mainTab === 'deleted') return true;
      
      if (statusTab === 'בתהליך') {
        return task.status === 'בתהליך' || task.status === 'במסלול';
      }
      return task.status === statusTab;
    })
    .map(task => {
    const assignee = users.find(u => u.id === task.assigneeId);
    const project = task && projects.find(p => p.id === task.projectId);
    const creatorId = task.creatorId || task.createdBy;
    const creator = users.find(u => u.id === creatorId);
    const taskDepartment = task && departments.find(d => d.id === task.departmentId);
    const taskTeam = task && teams.find(t => t.id === task.teamId);
    
    // צבעים דינמיים לפי סטטוס
    const statusColors = {
      'ממתין לאישור': 'from-yellow-500 to-orange-600',
      'ממתין לעבודה': 'from-gray-500 to-gray-600',
      'במסלול': 'from-blue-500 to-indigo-600',
      'הושלם': 'from-green-500 to-emerald-600'
    };

    const statusBgColors = {
      'ממתין לאישור': 'bg-yellow-50',
      'ממתין לעבודה': 'bg-gray-50',
      'במסלול': 'bg-blue-50',
      'הושלם': 'bg-green-50'
    };

    const statusBorderColors = {
      'ממתין לאישור': 'border-yellow-200',
      'ממתין לעבודה': 'border-gray-200',
      'במסלול': 'border-blue-200',
      'הושלם': 'border-green-200'
    };
    
    return (
      <div
        key={task.id}
        className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 ${statusBorderColors[task.status || 'ממתין לעבודה']}`}
      >
        {/* גרדיאנט עליון לפי סטטוס */}
        <div className={`h-2 bg-gradient-to-r ${statusColors[task.status || 'ממתין לעבודה']}`}></div>
        
        {/* תג "חדש" */}
        {task.isNew && !task.acknowledged && (
          <div className="absolute -top-2 -right-2 z-30">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg animate-pulse flex items-center gap-1">
              <span className="w-2 h-2 bg-white rounded-full animate-ping"></span>
              חדש!
            </div>
          </div>
        )}

        {/* כפתור "סמן כנקרא" */}
        {task.isNew && !task.acknowledged && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              updateTask(task.id, { 
                isNew: false,
                acknowledged: true 
              });
            }}
            className="absolute top-3 left-4 z-30 text-xs bg-purple-100 text-purple-700 px-3 py-1.5 rounded-lg hover:bg-purple-200 transition-all font-medium shadow-md"
          >
            ✓ סמן כנקרא
          </button>
        )}
        
        {/* תוכן הכרטיס */}
        <div 
          className="p-6 cursor-pointer" 
          onClick={() => navigate(`/tasks/${task.id}`)}
        >
<div 
  className="absolute top-14 left-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
  onClick={(e) => e.stopPropagation()}
>
  <div className="flex flex-row gap-2 items-center">
    {mainTab === 'deleted' ? (
      // Buttons for deleted tasks
      <>
        <button 
          onClick={async (e) => {
            e.stopPropagation();
            if (window.confirm(`האם לשחזר את המשימה "${task.title}"?`)) {
              await restoreTask(task.id);
              alert('המשימה שוחזרה בהצלחה!');
            }
          }}
          className="flex items-center justify-center p-2.5 bg-white rounded-lg shadow-md hover:shadow-xl text-gray-600 hover:text-green-600 hover:scale-105 transform transition-all w-10 h-10"
          title="שחזר משימה"
        >
          <CheckCircle size={18} />
        </button>
        <button 
          onClick={async (e) => {
            e.stopPropagation();
            if (window.confirm(`האם למחוק לצמיתות את המשימה "${task.title}"? לא ניתן לשחזר אחרי זה!`)) {
              await permanentDeleteTask(task.id);
              alert('המשימה נמחקה לצמיתות');
            }
          }}
          className="flex items-center justify-center p-2.5 bg-white rounded-lg shadow-md hover:shadow-xl text-gray-600 hover:text-red-600 hover:scale-105 transform transition-all w-10 h-10"
          title="מחק לצמיתות"
        >
          <XCircle size={18} />
        </button>
      </>
    ) : (
      // Regular task buttons
      <>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            setSelectedTask(task);
            const initialAssignees = Array.isArray(task.assigneeIds) && task.assigneeIds.length
              ? task.assigneeIds
              : (task.assigneeId ? [task.assigneeId] : []);
            setEditTaskForm({
              title: task.title || '',
              description: task.description || '',
              assigneeId: initialAssignees[0] || currentUser.id,
              assigneeIds: initialAssignees,
              teamMembers: task.teamMembers || [],
              priority: task.priority || 'important_not_urgent',
              dueDate: task.dueDate || '',
              dueTime: task.dueTime || '09:00',
              estimate: task.estimate || 0,
              status: task.status || 'ממתין לעבודה',
              projectId: task.projectId || '',
              departmentId: task.departmentId || '',
              subtasks: task.subtasks || []
            });
            setShowEditTask(true);
          }}
          className="flex items-center justify-center p-2.5 bg-white rounded-lg shadow-md hover:shadow-xl text-gray-600 hover:text-blue-600 hover:scale-105 transform transition-all w-10 h-10"
          title="ערוך משימה"
        >
          <Edit size={18} />
        </button>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            setSelectedTask(task);
            setShowStatusModal(true);
          }}
          className="flex items-center justify-center p-2.5 bg-white rounded-lg shadow-md hover:shadow-xl text-gray-600 hover:text-purple-600 hover:scale-105 transform transition-all w-10 h-10"
          title="שנה סטטוס"
        >
          <BarChart3 size={18} />
        </button>
        {canEditTask(task) && (
          <button 
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`האם אתה בטוח שברצונך למחוק את המשימה "${task.title}"?`)) {
                handleDeleteTask(task);
              }
            }}
            className="flex items-center justify-center p-2.5 bg-white rounded-lg shadow-md hover:shadow-xl text-gray-600 hover:text-red-600 hover:scale-105 transform transition-all w-10 h-10"
            title="מחק משימה"
          >
            <Trash2 size={18} />
          </button>
        )}
      </>
    )}
  </div>

</div>

          {/* סטטוס בדג' - למעלה באמצע */}
          <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-20">
            <div className={`px-3 py-1.5 rounded-lg ${statusBgColors[task.status || 'ממתין לעבודה']} border ${statusBorderColors[task.status || 'ממתין לעבודה']} text-xs font-semibold shadow-md`}>
              <StatusBadge status={task.status || 'ממתין לעבודה'} size="sm" type="task" />
            </div>
          </div>

          {/* אייקון משימה מעוצב */}
          <div className="mb-4 flex items-start">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${statusColors[task.status || 'ממתין לעבודה']} flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300`}>
              <CheckSquare className="w-8 h-8 text-white" />
            </div>
          </div>

          {/* כותרת ותיאור */}
          <div className="mb-4">
            <h3 className="font-bold text-xl text-gray-800 mb-2 group-hover:text-purple-600 transition-colors">
              {task.title}
            </h3>
            {mainTab === 'deleted' && task.deletedAt && (() => {
              const deletedDate = task.deletedAt?.toDate ? task.deletedAt.toDate() : new Date(task.deletedAt);
              const daysSinceDeleted = Math.floor((new Date() - deletedDate) / (1000 * 60 * 60 * 24));
              const daysRemaining = 14 - daysSinceDeleted;
              return (
                <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <Clock className="w-4 h-4 text-red-600" />
                  <span className="text-xs text-red-700 font-semibold">
                    תימחק לצמיתות בעוד {daysRemaining} ימים
                  </span>
                </div>
              );
            })()}
            <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">
              {task.description || 'אין תיאור זמין'}
            </p>
          </div>

          {/* תגיות */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-semibold ${
              task.scope === 'personal' 
                ? 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border border-purple-200' 
                : 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border border-green-200'
            }`}>
              {task.scope === 'personal' ? (
                <>
                  <User size={12} />
                  אישית
                </>
              ) : (
                <>
                  <Building2 size={12} />
                  ארגונית
                </>
              )}
            </span>
            
            {task.scope !== 'personal' && taskDepartment && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 text-xs rounded-lg font-medium border border-blue-200">
                <Building2 size={12} />
                {taskDepartment.name}
              </span>
            )}
            
            {taskTeam && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-xs rounded-lg font-medium border border-purple-200">
                <Users size={12} />
                {taskTeam.name}
              </span>
            )}
            
            {project && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 text-xs rounded-lg font-medium border border-green-200">
                <FolderOpen size={12} />
                {project.name}
              </span>
            )}
          </div>

          {/* מידע נוסף */}
          <div className="space-y-2.5 pt-4 border-t border-gray-100">
            {/* תתי משימות עם progress bar */}
            {task.subtasks && task.subtasks.length > 0 && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-3 rounded-xl border border-purple-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CheckSquare size={16} className="text-purple-600" />
                    <span className="text-sm font-bold text-purple-900">תתי משימות</span>
                  </div>
                  <span className="text-sm font-bold text-purple-700">
                    {task.subtasks.filter(st => {
                      if (typeof st === 'object' && st !== null) {
                        return st.completed;
                      }
                      return false; // String subtasks are never completed
                    }).length}/{task.subtasks.length}
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-purple-200 rounded-full h-2.5 mb-3">
                  <div
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 h-2.5 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${task.subtasks.length > 0 ? (task.subtasks.filter(st => {
                        if (typeof st === 'object' && st !== null) {
                          return st.completed;
                        }
                        return false; // String subtasks are never completed
                      }).length / task.subtasks.length) * 100 : 0}%` 
                    }}
                  />
                </div>
                
                {/* Subtasks List */}
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {task.subtasks.map((subtask, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        (() => {
                          if (typeof subtask === 'object' && subtask !== null) {
                            return subtask.completed;
                          }
                          return false; // String subtasks are never completed
                        })()
                          ? 'bg-green-500 border-green-500' 
                          : 'border-purple-400'
                      }`}>
                        {(() => {
                          if (typeof subtask === 'object' && subtask !== null && subtask.completed) {
                            return <CheckSquare size={10} className="text-white" />;
                          }
                          return null;
                        })()}
                      </div>
                      <span className={`flex-1 ${
                        (() => {
                          if (typeof subtask === 'object' && subtask !== null) {
                            return subtask.completed;
                          }
                          return false; // String subtasks are never completed
                        })()
                          ? 'text-green-700 line-through' 
                          : 'text-purple-700'
                      }`}>
                        {(() => {
                          // Handle different subtask formats
                          if (typeof subtask === 'string') {
                            return subtask;
                          }
                          if (typeof subtask === 'object' && subtask !== null) {
                            return subtask.title || subtask.name || subtask.description || subtask.text || subtask.content || `תת משימה ${idx + 1}`;
                          }
                          return `תת משימה ${idx + 1}`;
                        })()}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Completion Status */}
                <div className="text-xs text-purple-700 mt-2 pt-2 border-t border-purple-200">
                  {(() => {
                    const completedCount = task.subtasks.filter(st => {
                      if (typeof st === 'object' && st !== null) {
                        return st.completed;
                      }
                      return false; // String subtasks are never completed
                    }).length;
                    
                    if (completedCount === task.subtasks.length) {
                      return <span className="font-bold">✅ כל המשימות הושלמו!</span>;
                    } else {
                      return <span>{Math.round((completedCount / task.subtasks.length) * 100)}% הושלם</span>;
                    }
                  })()}
                </div>
              </div>
            )}
            
            {/* חברי צוות / משויך */}
            {(() => {
              const teamMembers = [];
              if (task.assigneeIds && task.assigneeIds.length > 0) {
                task.assigneeIds.forEach(id => {
                  const member = users.find(u => u.id === id);
                  if (member) teamMembers.push(member);
                });
              } else if (assignee) {
                teamMembers.push(assignee);
              }

              if (teamMembers.length === 0) return null;

              return (
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-3 rounded-xl border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={16} className="text-blue-600" />
                    <span className="text-sm font-bold text-blue-900">
                      {teamMembers.length === 1 ? 'משויך' : `${teamMembers.length} חברי צוות`}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {teamMembers.map((member, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-blue-300">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs">
                          {(member.name || member.email || 'U').charAt(0).toUpperCase()}
                        </div>
                        <span className="text-xs font-medium text-gray-700">
                          {member.name || member.email}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {creator && (
              <div className="flex items-center gap-2 text-gray-600">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <User size={16} className="text-green-600" />
                </div>
                <span className="text-sm font-medium">{creator?.name || 'לא ידוע'}</span>
              </div>
            )}
            
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <AlertTriangle size={16} className="text-orange-600" />
              </div>
              <span className="text-sm font-medium">
                {(() => {
                  const priorityLabels = {
                    'important_urgent': '🔥 חשוב ודחוף',
                    'important_not_urgent': '⭐ חשוב',
                    'not_important_urgent': '⚡ דחוף',
                    'not_important_not_urgent': '📝 רגיל'
                  };
                  return priorityLabels[task.priority] || 'לא מוגדרת';
                })()}
              </span>
            </div>

            {/* תאריך יעד */}
            {task.dueDate && (() => {
              const dueDate = new Date(task.dueDate);
              const now = new Date();
              const isOverdue = dueDate < now && task.status !== 'הושלם';
              const daysUntilDue = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
              
              return (
                <div className={`p-3 rounded-xl border-2 ${
                  isOverdue ? 'bg-gradient-to-r from-red-50 to-pink-50 border-red-300' :
                  daysUntilDue <= 3 && daysUntilDue > 0 ? 'bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-300' :
                  'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar size={16} className={
                      isOverdue ? 'text-red-600' :
                      daysUntilDue <= 3 && daysUntilDue > 0 ? 'text-orange-600' :
                      'text-green-600'
                    } />
                    <span className={`text-sm font-bold ${
                      isOverdue ? 'text-red-900' :
                      daysUntilDue <= 3 && daysUntilDue > 0 ? 'text-orange-900' :
                      'text-green-900'
                    }`}>
                      תאריך יעד
                    </span>
                  </div>
                  <div className="text-sm font-medium text-gray-700">
                    {dueDate.toLocaleDateString('he-IL')}
                    {task.dueTime && ` • ${task.dueTime}`}
                  </div>
                  {isOverdue && (
                    <div className="mt-2 px-2 py-1 bg-red-200 rounded-lg text-xs text-red-900 font-bold">
                      ⚠️ באיחור! ({Math.abs(daysUntilDue)} ימים)
                    </div>
                  )}
                  {!isOverdue && daysUntilDue <= 3 && daysUntilDue > 0 && (
                    <div className="mt-2 px-2 py-1 bg-orange-200 rounded-lg text-xs text-orange-900 font-bold">
                      🔔 נותרו {daysUntilDue} ימים
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* אינדיקטור למעבר */}
          <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity">
            <ChevronLeft className="w-6 h-6 text-purple-500 animate-pulse" />
          </div>
        </div>

        {/* זוהר בהאובר */}
        <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/0 to-pink-500/0 group-hover:from-purple-500/5 group-hover:to-pink-500/5 transition-all duration-300 pointer-events-none"></div>
      </div>
    );
  })}
</div> 

        {/* New Task Modal */}
        {showNewTask && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowNewTask(false)}
          >
            <div 
              ref={modalRef}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
           {/* Header - עיצוב מחודש */}
<div className="mb-8">
  <div className="flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center">
    <div>
      <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
        משימות
      </h1>
      <p className="text-gray-600">נהל ועקוב אחר כל המשימות שלך במקום אחד</p>
    </div>
    <button
      onClick={() => setShowNewTask(true)}
      className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 min-h-[44px] touch-manipulation font-semibold"
    >
      <Plus size={22} />
      משימה חדשה
    </button>
  </div>
</div>

              {/* Form Content */}
              <div className="p-6">
                <form onSubmit={handleCreateTask}>
                  {/* תחום משימה - בראש הטופס ומודגש */}
                  <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border-2 border-purple-300">
                    <label className="block text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <Globe size={20} className="text-purple-600" />
                      בחר סוג משימה
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setNewTask({...newTask, scope: 'personal'})}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          newTask.scope === 'personal'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-lg scale-105'
                            : 'border-gray-300 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <User size={24} className="mx-auto mb-2" />
                        <span className="font-semibold">משימה אישית</span>
                        <p className="text-xs mt-1">משימה פרטית שלך</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewTask({...newTask, scope: 'managerial'})}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          newTask.scope === 'managerial'
                            ? 'border-green-500 bg-green-50 text-green-700 shadow-lg scale-105'
                            : 'border-gray-300 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <Building2 size={24} className="mx-auto mb-2" />
                        <span className="font-semibold">משימה ארגונית</span>
                        <p className="text-xs mt-1">משימה למחלקה/צוות</p>
                      </button>
                    </div>
                  </div>

                  {/* הצגת שדות רק אחרי בחירת סוג משימה */}
                  {newTask.scope && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Left Column */}
                      <div className="space-y-4">
                        {/* Title */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <FileText size={16} className="text-purple-600" />
                            כותרת המשימה
                          </label>
                          <input
                            type="text"
                            value={newTask.title}
                            onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                            placeholder="הכנס כותרת למשימה..."
                            required
                          />
                        </div>
                        
                        {/* Description */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <FileText size={16} className="text-purple-600" />
                            תיאור המשימה
                          </label>
                          <textarea
                            value={newTask.description}
                            onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                            onKeyDown={handleKeyDown}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all h-24 resize-none"
                            placeholder="תאר את המשימה בפירוט..."
                            rows="3"
                          />
                        </div>

                        {/* Priority */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <AlertTriangle size={16} className="text-purple-600" />
                            עדיפות
                          </label>
                          <select
                            value={newTask.priority}
                            onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                          >
                            <option value="important_urgent">🔥 חשוב ודחוף</option>
                            <option value="important_not_urgent">⭐ חשוב ולא דחוף</option>
                            <option value="not_important_urgent">⚡ לא חשוב ודחוף</option>
                            <option value="not_important_not_urgent">📝 לא חשוב ולא דחוף</option>
                          </select>
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="space-y-4">
                        {/* Assignee Selection - Show for all scopes */}
                        {newTask.scope && (
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                                <User size={16} className="text-purple-600" />
                                הקצה משימה ל
                              </label>
                              {newTask.assigneeIds.length > 0 && (
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                                  {newTask.assigneeIds.length} נבחרו
                                </span>
                              )}
                            </div>
                            <div className="space-y-2">
                              <div className="relative">
                                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                  type="text"
                                  placeholder="חפש עובד לפי שם או אימייל..."
                                  value={assigneeDeptFilter}
                                  onChange={(e) => setAssigneeDeptFilter(e.target.value)}
                                  className="w-full pr-10 pl-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                                />
                              </div>
                              <div className="max-h-64 overflow-y-auto border-2 border-gray-200 rounded-xl p-3 bg-gray-50">
                                <div className="space-y-2">
                                  {users
                                    .filter(user => 
                                      user.name.toLowerCase().includes(assigneeDeptFilter.toLowerCase()) ||
                                      user.email.toLowerCase().includes(assigneeDeptFilter.toLowerCase())
                                    )
                                    .map(user => (
                                      <label 
                                        key={user.id} 
                                        className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                                          newTask.assigneeIds.includes(user.id)
                                            ? 'bg-purple-50 border-purple-300 shadow-md'
                                            : 'bg-white border-gray-200 hover:border-purple-200 hover:bg-purple-50'
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={newTask.assigneeIds.includes(user.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setNewTask({
                                                ...newTask,
                                                assigneeIds: [...newTask.assigneeIds, user.id],
                                                assigneeId: newTask.assigneeIds.length === 0 ? user.id : newTask.assigneeId
                                              });
                                            } else {
                                              const updatedIds = newTask.assigneeIds.filter(id => id !== user.id);
                                              setNewTask({
                                                ...newTask,
                                                assigneeIds: updatedIds,
                                                assigneeId: updatedIds.length > 0 ? updatedIds[0] : ''
                                              });
                                            }
                                          }}
                                          className="w-5 h-5 text-purple-600 rounded-md border-2 border-gray-300 focus:ring-2 focus:ring-purple-500 cursor-pointer"
                                        />
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold shadow-md">
                                          {user.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex-1">
                                          <p className="font-semibold text-gray-900">{user.name}</p>
                                          <p className="text-xs text-gray-500">{user.email}</p>
                                        </div>
                                      </label>
                                    ))}
                                  {users.filter(user => 
                                    user.name.toLowerCase().includes(assigneeDeptFilter.toLowerCase()) ||
                                    user.email.toLowerCase().includes(assigneeDeptFilter.toLowerCase())
                                  ).length === 0 && (
                                    <div className="text-center py-8 text-gray-500">
                                      <User className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                      <p>לא נמצאו עובדים</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Dynamic Fields Based on Scope */}
                        {newTask.scope === 'managerial' ? (
                          <>
                            {/* Department Assignment */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                <Building2 size={16} className="text-purple-600" />
                                הקצאה למחלקה
                              </label>
                              <select
                                value={newTask.departmentId}
                                onChange={(e) => setNewTask({ ...newTask, departmentId: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                              >
                                <option value="">בחר מחלקה</option>
                                {departments && departments.map(dept => (
                                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                                ))}
                              </select>
                            </div>

                            {/* Team Assignment */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                <Users size={16} className="text-purple-600" />
                                הקצאה לצוות
                              </label>
                              <select
                                value={newTask.teamId || ''}
                                onChange={(e) => setNewTask({ ...newTask, teamId: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                              >
                                <option value="">בחר צוות</option>
                                {teams && teams
                                  .filter(team => !newTask.departmentId || team.departmentId === newTask.departmentId)
                                  .filter(team => team.isActive)
                                  .map(team => (
                                    <option key={team.id} value={team.id}>
                                      {team.name}
                                      {team.departmentId && ` - ${departments.find(d => d.id === team.departmentId)?.name || 'לא ידוע'}`}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </>
                        ) : null}

                        {/* Project */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <FolderOpen size={16} className="text-purple-600" />
                            פרויקט
                          </label>
                          <select
                            value={newTask.projectId}
                            onChange={(e) => setNewTask({...newTask, projectId: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                          >
                            <option value="">ללא פרויקט</option>
                            {projects
                              .filter(project => {
                                // סינון לפי תפקיד
                                if (currentUser?.role === 'employee') {
                                  const hasAccess = (project.scope === 'personal' && project.assigneeId === currentUser.id) || 
                                    project.assigneeId === currentUser.id || 
                                    (Array.isArray(project.teamMembers) && project.teamMembers.includes(currentUser.id));
                                  if (!hasAccess) return false;
                                }
                                
                                // סינון לפי מחלקה - אם נבחרה מחלקה
                                if (newTask.departmentId) {
                                  return project.departmentId === newTask.departmentId;
                                }
                                
                                // אם אין מחלקה נבחרת, הצג את כל הפרויקטים
                                return true;
                              })
                              .map(project => (
                                <option key={project.id} value={project.id}>
                                  {project.name}
                                  {!newTask.departmentId && project.departmentId && 
                                    ` - ${departments.find(d => d.id === project.departmentId)?.name || 'לא ידוע'}`
                                  }
                                </option>
                              ))}
                          </select>
                          {newTask.departmentId && projects.filter(p => p.departmentId === newTask.departmentId).length === 0 && (
                            <p className="text-xs text-gray-500 mt-1">אין פרויקטים במחלקה זו</p>
                          )}
                        </div>

                        {/* Date with improved picker */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <Calendar size={16} className="text-purple-600" />
                            תאריך יעד
                          </label>
                          <input
                            type="date"
                            value={newTask.dueDate}
                            onChange={(e) => {
                              setNewTask({...newTask, dueDate: e.target.value});
                              setShowDatePicker(false);
                            }}
                            onBlur={() => setShowDatePicker(false)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all cursor-pointer"
                          />
                        </div>

                        {/* Time with improved picker */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <Clock size={16} className="text-purple-600" />
                            שעת יעד
                          </label>
                          <input
                            type="time"
                            value={newTask.dueTime}
                            onChange={(e) => {
                              setNewTask({...newTask, dueTime: e.target.value});
                              setShowTimePicker(false);
                            }}
                            onBlur={() => setShowTimePicker(false)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all cursor-pointer"
                          />
                        </div>
                      </div>

                      {/* Full Width Estimate Section */}
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <Clock size={16} className="text-purple-600" />
                          הערכת זמן
                        </label>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">ימים</label>
                            <input
                              type="number"
                              value={estimateDays}
                              onChange={(e) => setEstimateDays(parseInt(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                              min="0"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">שעות</label>
                            <input
                              type="number"
                              value={estimateHours}
                              onChange={(e) => setEstimateHours(parseInt(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                              min="0"
                              max="23"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">דקות</label>
                            <input
                              type="number"
                              value={estimateMinutes}
                              onChange={(e) => setEstimateMinutes(parseInt(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                              min="0"
                              max="59"
                              placeholder="0"
                            />
                          </div>
                        </div>
                        {(estimateDays > 0 || estimateHours > 0 || estimateMinutes > 0) && (
                          <p className="text-xs text-gray-500 mt-2">
                            סה"כ: {estimateDays > 0 && `${estimateDays} ימים`} {estimateHours > 0 && `${estimateHours} שעות`} {estimateMinutes > 0 && `${estimateMinutes} דקות`}
                          </p>
                        )}
                      </div>

                      {/* Subtasks - Full Width */}
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <List size={16} className="text-purple-600" />
                          תתי משימות
                        </label>
                        <div className="space-y-2">
                          {newTask.subtasks && newTask.subtasks.length > 0 && (
                            <div className="space-y-2">
                              {newTask.subtasks.map((subtask, index) => (
                                <div key={index} className="flex items-center gap-2">
                                  <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                                    {typeof subtask === 'string' ? subtask : subtask.text}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updatedSubtasks = newTask.subtasks.filter((_, i) => i !== index);
                                      setNewTask({...newTask, subtasks: updatedSubtasks});
                                    }}
                                    className="w-8 h-8 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg flex items-center justify-center transition-all"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newSubtask || ''}
                              onChange={(e) => setNewSubtask(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && newSubtask.trim()) {
                                  e.preventDefault();
                                  const updatedSubtasks = [...(newTask.subtasks || []), newSubtask.trim()];
                                  setNewTask({...newTask, subtasks: updatedSubtasks});
                                  setNewSubtask('');
                                }
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                              placeholder="הוסף תת משימה חדשה..."
                            />
                            <button
                              type="button"
                              onClick={() => {
                                if (newSubtask && newSubtask.trim()) {
                                  const updatedSubtasks = [...(newTask.subtasks || []), newSubtask.trim()];
                                  setNewTask({...newTask, subtasks: updatedSubtasks});
                                  setNewSubtask('');
                                }
                              }}
                              className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg flex items-center gap-2 transition-all font-medium"
                            >
                              <Plus size={16} />
                              הוסף
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="mt-8 flex gap-3 justify-end pt-6 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setShowNewTask(false)}
                      className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all font-medium"
                    >
                      ביטול
                    </button>
                    <button
                      type="submit"
                      disabled={!newTask.scope}
                      className={`px-8 py-3 rounded-xl font-semibold shadow-lg transform transition-all ${
                        newTask.scope
                          ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800 hover:shadow-xl hover:scale-105'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Plus size={20} />
                        צור משימה
                      </div>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Task detail view
  const assignee = users.find(u => u.id === selectedTask.assigneeId);
  const assignees = Array.isArray(selectedTask.assigneeIds) && selectedTask.assigneeIds.length
    ? selectedTask.assigneeIds.map(id => users.find(u => u.id === id)).filter(Boolean)
    : (assignee ? [assignee] : []);
  const creatorId = selectedTask.creatorId || selectedTask.createdBy;
  const creator = users.find(u => u.id === creatorId);
  const project = projects.find(p => p.id === selectedTask.projectId);
  const taskTeam = teams.find(t => t.id === selectedTask.teamId);
  const taskDepartment = departments.find(d => d.id === selectedTask.departmentId); 
  
  // Tabs for task details
  const taskTabs = [
    {
      id: 'details',
      label: 'פרטים',
      icon: Info,
      content: <TaskDetails task={selectedTask} assignee={assignee} creator={creator} project={project} team={taskTeam} department={taskDepartment} />
    },
    {
      id: 'comments',
      label: 'הערות',
      icon: MessageSquare,
      content: <TaskComments task={selectedTask} />
    },
    {
      id: 'activity',
      label: 'פעילות',
      icon: Activity,
      content: <TaskActivity task={selectedTask} />
    },
    {
      id: 'files',
      label: 'קבצים',
      icon: Paperclip,
      content: <TaskFiles task={selectedTask} />
    }
  ];

  return (
    <div className="p-6">
    {/* Header - עיצוב מחודש */}
<div className="mb-8">
  <button
    onClick={() => {
      navigate('/tasks');
      setSelectedTask(null);
    }}
    className="flex items-center gap-2 text-gray-600 hover:text-purple-600 mb-6 transition-colors group"
  >
    <div className="w-8 h-8 bg-gray-100 group-hover:bg-purple-100 rounded-lg flex items-center justify-center transition-colors">
      <ChevronLeft size={20} />
    </div>
    <span className="font-medium">חזרה למשימות</span>
  </button>
  
  <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
    {/* גרדיאנט עליון */}
    <div className={`h-3 bg-gradient-to-r ${(() => {
      const statusColors = {
        'ממתין לאישור': 'from-yellow-500 to-orange-600',
        'ממתין לעבודה': 'from-gray-500 to-gray-600',
        'במסלול': 'from-blue-500 to-indigo-600',
        'הושלם': 'from-green-500 to-emerald-600'
      };
      return statusColors[selectedTask.status || 'ממתין לעבודה'];
    })()}`}></div>

    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          {/* אייקון ושם המשימה */}
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${(() => {
              const statusColors = {
                'ממתין לאישור': 'from-yellow-500 to-orange-600',
                'ממתין לעבודה': 'from-gray-500 to-gray-600',
                'במסלול': 'from-blue-500 to-indigo-600',
                'הושלם': 'from-green-500 to-emerald-600'
              };
              return statusColors[selectedTask.status || 'ממתין לעבודה'];
            })()} flex items-center justify-center shadow-lg`}>
              <CheckSquare className="w-10 h-10 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">{selectedTask.title}</h1>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>נוצר על ידי {creator?.name}</span>
                <span>•</span>
                <span>{new Date(selectedTask.createdAt?.toDate ? selectedTask.createdAt.toDate() : selectedTask.createdAt).toLocaleDateString('he-IL')}</span>
              </div>
            </div>
          </div>
          
          {/* תיאור המשימה */}
          {selectedTask.description && (
            <p className="text-lg text-gray-700 leading-relaxed mt-4 p-4 bg-gray-50 rounded-xl border-r-4 border-purple-500">
              {selectedTask.description}
            </p>
          )}
        </div>
        
        {/* תגיות סטטוס ועדיפות */}
        <div className="flex flex-col gap-3">
          <div className={`px-6 py-3 rounded-2xl text-lg font-bold shadow-lg ${(() => {
            const statusBg = {
              'ממתין לאישור': 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300',
              'ממתין לעבודה': 'bg-gray-100 text-gray-700 border-2 border-gray-300',
              'במסלול': 'bg-blue-100 text-blue-700 border-2 border-blue-300',
              'הושלם': 'bg-green-100 text-green-700 border-2 border-green-300'
            };
            return statusBg[selectedTask.status || 'ממתין לעבודה'];
          })()}`}>
            <StatusBadge status={selectedTask.status || 'במסלול'} type="task" />
          </div>
          <div className="px-6 py-3 rounded-2xl bg-orange-100 border-2 border-orange-300 shadow-lg">
            <PriorityBadge priority={selectedTask.priority || 'important_not_urgent'} />
          </div>
        </div>
      </div>

      {/* כפתורי פעולה */}
      {canEditTask(selectedTask) && (
        <div className="flex gap-3 pt-6 border-t border-gray-200">
          <button
            className="flex items-center gap-2 px-4 py-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all font-medium shadow-sm hover:shadow-md"
            onClick={() => {
              const initialAssignees = Array.isArray(selectedTask.assigneeIds) && selectedTask.assigneeIds.length
                ? selectedTask.assigneeIds
                : (selectedTask.assigneeId ? [selectedTask.assigneeId] : []);
              setEditTaskForm({
                title: selectedTask.title || '',
                description: selectedTask.description || '',
                assigneeId: initialAssignees[0] || currentUser.id,
                assigneeIds: initialAssignees,
                teamMembers: selectedTask.teamMembers || [],
                priority: selectedTask.priority || 'important_not_urgent',
                dueDate: selectedTask.dueDate || '',
                estimate: selectedTask.estimate || 0,
                status: selectedTask.status || 'במסלול',
                projectId: selectedTask.projectId || ''
              });
              setShowEditTask(true);
            }}
          >
            <Edit size={20} />
            ערוך משימה
          </button>
          
          <button
            className="flex items-center gap-2 px-4 py-2 text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-xl transition-all font-medium shadow-sm hover:shadow-md"
            onClick={() => {
              setShowStatusModal(true);
            }}
          >
            <BarChart3 size={20} />
            שנה סטטוס
          </button>

          <button
            className="flex items-center gap-2 px-4 py-2 text-green-600 bg-green-50 hover:bg-green-100 rounded-xl transition-all font-medium shadow-sm hover:shadow-md"
            onClick={() => {
              const initialAssignees = Array.isArray(selectedTask.assigneeIds) && selectedTask.assigneeIds.length
                ? selectedTask.assigneeIds
                : (selectedTask.assigneeId ? [selectedTask.assigneeId] : []);
              setEditTaskForm({
                title: selectedTask.title || '',
                description: selectedTask.description || '',
                assigneeId: initialAssignees[0] || currentUser.id,
                assigneeIds: initialAssignees,
                teamMembers: selectedTask.teamMembers || [],
                priority: selectedTask.priority || 'important_not_urgent',
                dueDate: selectedTask.dueDate || '',
                estimate: selectedTask.estimate || 0,
                status: selectedTask.status || 'במסלול',
                projectId: selectedTask.projectId || ''
              });
              setShowEditTask(true);
            }}
          >
            <Users size={20} />
            הוסף עובדים
          </button>
          
          <button 
            className="flex items-center gap-2 px-4 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-all font-medium shadow-sm hover:shadow-md"
            onClick={() => {
              if (window.confirm(`האם אתה בטוח שברצונך למחוק את המשימה "${selectedTask.title}"?`)) {
                handleDeleteTask(selectedTask);
                navigate('/tasks');
              }
            }}
          >
            <Trash2 size={20} />
            מחק משימה
          </button>
        </div>
      )}
    </div>
  </div>
</div>

      {/* Acknowledgment Alert */}
      {!selectedTask.acknowledged && selectedTask.assigneeId === currentUser.id && (creatorId !== currentUser.id) && (
        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-yellow-600" size={20} />
              <span className="font-medium">משימה זו ממתינה לאישור קבלה</span>
            </div>
            <button
              onClick={handleAcknowledge}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              קיבלתי/ראיתי
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <TabsComponent 
        tabs={taskTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

{/* Status Change Modal - עיצוב מחודש */}
{showStatusModal && selectedTask && (
  <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4 rounded-t-2xl">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <BarChart3 size={24} />
          שנה סטטוס משימה
        </h2>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="mb-6">
          <p className="text-gray-600 mb-2">משימה: <strong className="text-gray-900">{selectedTask.title}</strong></p>
          <p className="text-sm text-gray-500">בחר סטטוס חדש למשימה</p>
        </div>
        
        <div className="space-y-3 mb-6">
          {[
            { value: 'ממתין לעבודה', icon: '⏳', color: 'gray' },
            { value: 'ממתין לאישור', icon: '⏰', color: 'yellow' },
            { value: 'במסלול', icon: '🚀', color: 'blue' },
            { value: 'הושלם', icon: '✅', color: 'green' }
          ].map(({ value, icon, color }) => (
            <button
              key={value}
              onClick={async () => {
                try {
                  await updateTask(selectedTask.id, { status: value });
                  setShowStatusModal(false);
                } catch (error) {
                  console.error('Error updating task status:', error);
                  alert('שגיאה בעדכון סטטוס המשימה');
                }
              }}
              className={`w-full p-4 text-right rounded-xl border-2 transition-all duration-300 flex items-center gap-3 ${
                selectedTask.status === value
                  ? `bg-${color}-50 border-${color}-300 shadow-lg scale-105`
                  : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-purple-300 hover:shadow-md'
              }`}
            >
              <span className="text-2xl">{icon}</span>
              <span className={`flex-1 font-semibold ${
                selectedTask.status === value ? `text-${color}-700` : 'text-gray-700'
              }`}>
                {value}
              </span>
              {selectedTask.status === value && (
                <span className={`px-3 py-1 bg-${color}-100 text-${color}-700 text-xs rounded-full font-bold`}>
                  נוכחי
                </span>
              )}
            </button>
          ))}
        </div>
        
        <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
          <button
            onClick={() => setShowStatusModal(false)}
            className="px-6 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all font-medium"
          >
            ביטול
          </button>
        </div>
      </div>
    </div>
  </div>
)}
      {/* Edit Task Modal */}
      {showEditTask && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">הוסף עובדים למשימה</h2>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                console.log('Updating task with:', editTaskForm);
                console.log('Assignee IDs:', editTaskForm.assigneeIds);
                await updateTask(selectedTask.id, editTaskForm);
                setShowEditTask(false);
                alert('המשימה עודכנה בהצלחה!');
              } catch (error) {
                console.error('Error updating task:', error);
                alert('שגיאה בעדכון המשימה');
              }
            }}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">כותרת</label>
                  <input
                    type="text"
                    value={editTaskForm.title}
                    onChange={(e) => setEditTaskForm({...editTaskForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">סטטוס</label>
                  <select
                    value={editTaskForm.status}
                    onChange={(e) => setEditTaskForm({...editTaskForm, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ממתין לעבודה">ממתין לעבודה</option>
                    <option value="ממתין לאישור">ממתין לאישור</option>
                    <option value="במסלול">במסלול</option>
                    <option value="הושלם">הושלם</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">עדיפות</label>
                  <select
                    value={editTaskForm.priority}
                    onChange={(e) => setEditTaskForm({...editTaskForm, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="important_urgent">חשוב ודחוף</option>
                    <option value="important_not_urgent">חשוב ולא דחוף</option>
                    <option value="not_important_urgent">לא חשוב ודחוף</option>
                    <option value="not_important_not_urgent">לא חשוב ולא דחוף</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">מחלקה</label>
                  <select
                    value={editTaskForm.departmentId || ''}
                    onChange={(e) => setEditTaskForm({...editTaskForm, departmentId: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">ללא מחלקה</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">פרויקט</label>
                  <select
                    value={editTaskForm.projectId || ''}
                    onChange={(e) => setEditTaskForm({...editTaskForm, projectId: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">ללא פרויקט</option>
                    {projects
                      .filter(project => {
                        // אם יש מחלקה נבחרת, הצג רק פרויקטים מאותה מחלקה
                        if (editTaskForm.departmentId) {
                          return project.departmentId === editTaskForm.departmentId;
                        }
                        // אם אין מחלקה, הצג את כל הפרויקטים
                        return true;
                      })
                      .map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                          {!editTaskForm.departmentId && project.departmentId && 
                            ` - ${departments.find(d => d.id === project.departmentId)?.name || ''}`
                          }
                        </option>
                      ))}
                  </select>
                  {editTaskForm.departmentId && projects.filter(p => p.departmentId === editTaskForm.departmentId).length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">אין פרויקטים במחלקה זו</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">תאריך יעד</label>
                  <input
                    type="date"
                    value={editTaskForm.dueDate}
                    onChange={(e) => setEditTaskForm({...editTaskForm, dueDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">שעת יעד</label>
                  <input
                    type="time"
                    value={editTaskForm.dueTime}
                    onChange={(e) => setEditTaskForm({...editTaskForm, dueTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">תיאור</label>
                <textarea
                  value={editTaskForm.description}
                  onChange={(e) => setEditTaskForm({...editTaskForm, description: e.target.value})}
                  onKeyDown={handleKeyDown}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 h-24"
                  rows="3"
                />
              </div>

              {/* Subtasks Section */}
              <div className="mb-6 border-t pt-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">תתי משימות</label>
                <div className="space-y-3">
                  {/* Add new subtask */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={editSubtask}
                      onChange={(e) => setEditSubtask(e.target.value)}
                      placeholder="הוסף תת משימה חדשה..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && editSubtask.trim()) {
                          e.preventDefault();
                          const currentSubtasks = editTaskForm.subtasks || [];
                          setEditTaskForm({
                            ...editTaskForm,
                            subtasks: [...currentSubtasks, { text: editSubtask, completed: false }]
                          });
                          setEditSubtask('');
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (editSubtask.trim()) {
                          const currentSubtasks = editTaskForm.subtasks || [];
                          setEditTaskForm({
                            ...editTaskForm,
                            subtasks: [...currentSubtasks, { text: editSubtask, completed: false }]
                          });
                          setEditSubtask('');
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus size={16} />
                    </button>
                  </div>

                  {/* Existing subtasks */}
                  {editTaskForm.subtasks && editTaskForm.subtasks.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {editTaskForm.subtasks.map((subtask, index) => {
                        // Support both old and new subtask formats
                        const subtaskText = typeof subtask === 'string' ? subtask : (subtask.text || subtask.title || subtask.name || '');
                        const subtaskCompleted = typeof subtask === 'object' ? (subtask.completed || false) : false;
                        
                        return (
                        <div key={index} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                          <input
                            type="checkbox"
                            checked={subtaskCompleted}
                            onChange={() => {
                              const updatedSubtasks = [...editTaskForm.subtasks];
                              if (typeof updatedSubtasks[index] === 'string') {
                                updatedSubtasks[index] = { text: updatedSubtasks[index], completed: true };
                              } else {
                                updatedSubtasks[index] = {
                                  ...updatedSubtasks[index],
                                  completed: !updatedSubtasks[index].completed
                                };
                              }
                              setEditTaskForm({
                                ...editTaskForm,
                                subtasks: updatedSubtasks
                              });
                            }}
                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                          />
                          <span className={`flex-1 font-medium ${subtaskCompleted ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                            {subtaskText || '(ללא טקסט)'}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedSubtasks = editTaskForm.subtasks.filter((_, i) => i !== index);
                              setEditTaskForm({
                                ...editTaskForm,
                                subtasks: updatedSubtasks
                              });
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded transition-colors flex-shrink-0"
                          >
                            <X size={18} />
                          </button>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Assignees Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                    <User size={16} className="text-blue-600" />
                    עובדים מוקצים
                  </label>
                  {editTaskForm.assigneeIds.length > 0 && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                      {editTaskForm.assigneeIds.length} נבחרו
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="חפש עובד לפי שם או אימייל..."
                      value={editAssigneeDeptFilter}
                      onChange={(e) => setEditAssigneeDeptFilter(e.target.value)}
                      className="w-full pr-10 pl-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto border-2 border-gray-200 rounded-xl p-3 bg-gray-50">
                    <div className="space-y-2">
                      {users
                        .filter(user => user.isActive)
                        .filter(user => 
                          user.name.toLowerCase().includes(editAssigneeDeptFilter.toLowerCase()) ||
                          user.email.toLowerCase().includes(editAssigneeDeptFilter.toLowerCase())
                        )
                        .map(user => (
                          <label 
                            key={user.id} 
                            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                              editTaskForm.assigneeIds.includes(user.id)
                                ? 'bg-blue-50 border-blue-300 shadow-md'
                                : 'bg-white border-gray-200 hover:border-blue-200 hover:bg-blue-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={editTaskForm.assigneeIds.includes(user.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setEditTaskForm({
                                    ...editTaskForm,
                                    assigneeIds: [...editTaskForm.assigneeIds, user.id],
                                    assigneeId: editTaskForm.assigneeIds.length === 0 ? user.id : editTaskForm.assigneeId
                                  });
                                } else {
                                  const updatedIds = editTaskForm.assigneeIds.filter(id => id !== user.id);
                                  setEditTaskForm({
                                    ...editTaskForm,
                                    assigneeIds: updatedIds,
                                    assigneeId: updatedIds.length > 0 ? updatedIds[0] : ''
                                  });
                                }
                              }}
                              className="w-5 h-5 text-blue-600 rounded-md border-2 border-gray-300 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                            />
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold shadow-md">
                              {user.name?.charAt(0)?.toUpperCase() || 'מ'}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{user.name}</p>
                              <p className="text-xs text-gray-500">{user.email}</p>
                            </div>
                          </label>
                        ))}
                      {users
                        .filter(user => user.isActive)
                        .filter(user => 
                          user.name.toLowerCase().includes(editAssigneeDeptFilter.toLowerCase()) ||
                          user.email.toLowerCase().includes(editAssigneeDeptFilter.toLowerCase())
                        ).length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <User className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p>לא נמצאו עובדים</p>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowEditTask(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all"
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all"
                >
                  שמור עובדים
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Task Details Component - עיצוב מחודש
const TaskDetails = ({ task, assignee, creator, project, team, department }) => {
  const [completedSubtasks, setCompletedSubtasks] = useState([]);
  const { updateTask, users } = useData();
  
  const handleSubtaskToggle = async (subtaskIndex) => {
    const newCompletedSubtasks = completedSubtasks.includes(subtaskIndex)
      ? completedSubtasks.filter(i => i !== subtaskIndex)
      : [...completedSubtasks, subtaskIndex];
    
    setCompletedSubtasks(newCompletedSubtasks);
  };

  useEffect(() => {
    if (task.status === 'הושלם' && task.subtasks) {
      setCompletedSubtasks(task.subtasks.map((_, index) => index));
    }
  }, [task.status, task.subtasks]);
  
  return (
    <div className="space-y-6">
      {/* תתי משימות */}
      {task.subtasks && task.subtasks.length > 0 && (
        <div className="bg-gradient-to-br from-white to-purple-50 rounded-2xl p-6 border-2 border-purple-200 shadow-lg">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
              <List size={20} className="text-white" />
            </div>
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              תתי משימות
            </span>
            <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-bold">
              {completedSubtasks.length}/{task.subtasks.length}
            </span>
          </h3>
          
          {/* פס התקדמות */}
          <div className="mb-4">
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
              <div 
                className="h-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full transition-all duration-500"
                style={{ width: `${(completedSubtasks.length / task.subtasks.length) * 100}%` }}
              ></div>
            </div>
          </div>

          <div className="space-y-3">
            {task.subtasks.map((subtask, index) => {
              const isCompleted = completedSubtasks.includes(index) || task.status === 'הושלם';
              return (
                <div 
                  key={index} 
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-300 ${
                    isCompleted 
                      ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 shadow-md' 
                      : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-purple-300 hover:shadow-md'
                  }`}
                >
                  <button
                    onClick={() => handleSubtaskToggle(index)}
                    className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all ${
                      isCompleted
                        ? 'bg-gradient-to-br from-green-500 to-emerald-600 border-green-500 shadow-lg'
                        : 'bg-white border-gray-300 hover:border-purple-500 hover:shadow-md'
                    }`}
                    disabled={task.status === 'הושלם'}
                  >
                    {isCompleted && (
                      <CheckCircle size={16} className="text-white" />
                    )}
                  </button>
                  <span className={`flex-1 font-medium ${isCompleted ? 'line-through text-gray-500' : 'text-gray-800'}`}>
                    {typeof subtask === 'string' ? subtask : subtask.text}
                  </span>
                  {isCompleted && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                      ✓ הושלם
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* פרטי המשימה */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-gray-200 shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <Info className="w-6 h-6" />
            פרטי המשימה
          </h3>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* כותרת */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                  <FileText size={20} className="text-blue-600" />
                </div>
                <p className="text-sm font-medium text-gray-600">כותרת</p>
              </div>
              <p className="font-bold text-lg text-gray-800 mr-13">{task.title}</p>
            </div>
            
            {/* סטטוס */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg flex items-center justify-center">
                  <BarChart3 size={20} className="text-purple-600" />
                </div>
                <p className="text-sm font-medium text-gray-600">סטטוס</p>
              </div>
              <p className="font-bold text-lg text-gray-800 mr-13">{task.status}</p>
            </div>
            
            {/* עדיפות */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg flex items-center justify-center">
                  <AlertTriangle size={20} className="text-orange-600" />
                </div>
                <p className="text-sm font-medium text-gray-600">עדיפות</p>
              </div>
              <p className="font-bold text-lg text-gray-800 mr-13">
                {(() => {
                  const priorityLabels = {
                    'important_urgent': '🔥 חשוב ודחוף',
                    'important_not_urgent': '⭐ חשוב ולא דחוף',
                    'not_important_urgent': '⚡ דחוף ולא חשוב',
                    'not_important_not_urgent': '📝 לא חשוב ולא דחוף'
                  };
                  return priorityLabels[task.priority] || 'לא מוגדרת';
                })()}
              </p>
            </div>
            
            {/* תאריך יעד */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-red-200 rounded-lg flex items-center justify-center">
                  <Calendar size={20} className="text-red-600" />
                </div>
                <p className="text-sm font-medium text-gray-600">תאריך ושעת יעד</p>
              </div>
              <p className="font-bold text-lg text-gray-800 mr-13">
                {task.dueDate ? (
                  `${new Date(task.dueDate).toLocaleDateString('he-IL')}${task.dueTime ? ` • ${task.dueTime}` : ''}`
                ) : 'לא נקבע'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* חברי הצוות במשימה */}
      <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl border-2 border-blue-200 shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <Users className="w-6 h-6" />
            חברי הצוות במשימה
          </h3>
        </div>
        
        <div className="p-6">
          {(() => {
            const assigneeIds = task.assigneeIds || (task.assigneeId ? [task.assigneeId] : []);
            const teamMembers = assigneeIds.map(id => users.find(u => u.id === id)).filter(Boolean);
            
            if (teamMembers.length === 0) {
              return (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                  <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users size={40} className="text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-lg">אין עובדים מוקצים למשימה זו</p>
                </div>
              );
            }
            
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {teamMembers.map((member, index) => (
                  <div key={member.id} className="bg-gradient-to-br from-white to-blue-50 rounded-xl p-4 border-2 border-blue-200 hover:shadow-lg transition-all duration-300 hover:scale-105">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                        {member.name?.charAt(0) || 'מ'}
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-gray-900">{member.name}</p>
                        <p className="text-sm text-gray-600">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                        {member.role || 'עובד'}
                      </p>
                      {index === 0 && (
                        <span className="px-2 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs rounded-full font-bold shadow-md">
                          ראשי
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      {/* נוצר על ידי */}
      {creator && (
        <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-lg hover:shadow-xl transition-shadow">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
              <User size={20} className="text-white" />
            </div>
            נוצר על ידי
          </h3>
          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
              {creator.name?.charAt(0) || 'מ'}
            </div>
            <div>
              <p className="font-bold text-gray-900">{creator.name}</p>
              <p className="text-sm text-gray-600">{creator.email}</p>
            </div>
          </div>
        </div>
      )}

      {/* מחלקה */}
      {task.scope !== 'personal' && department && (
        <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-lg hover:shadow-xl transition-shadow">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <Building2 size={20} className="text-white" />
            </div>
            מחלקה
          </h3>
          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
              <Building2 size={24} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-gray-900">{department.name}</p>
              {department.description && (
                <p className="text-sm text-gray-600">{department.description}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* פרויקט */}
      {project && (
        <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-lg hover:shadow-xl transition-shadow">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
              <FolderOpen size={20} className="text-white" />
            </div>
            פרויקט
          </h3>
          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg">
              <FolderOpen size={24} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-gray-900">{project.name}</p>
              <p className="text-sm text-gray-600">{project.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* צוות */}
      {team && (
        <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-lg hover:shadow-xl transition-shadow">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
              <Users size={20} className="text-white" />
            </div>
            צוות
          </h3>
          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border border-orange-200">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center shadow-lg">
              <Users size={24} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-gray-900">{team.name}</p>
              <p className="text-sm text-gray-600">{team.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Task Comments Component
const TaskComments = ({ task }) => {
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState([]);
  const { currentUser } = useAuth();
  const { updateTask, createComment } = useData();

  useEffect(() => {
    if (task.comments) {
      setComments(task.comments);
    }
  }, [task.comments]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    const newCommentObj = {
      id: Date.now().toString(),
      text: newComment,
      authorId: currentUser.id,
      authorName: currentUser.name,
      createdAt: new Date().toISOString()
    };

    const updatedComments = [...comments, newCommentObj];
    
    await updateTask(task.id, { comments: updatedComments });
    await createComment({
      taskId: task.id,
      content: newComment,
      userId: currentUser.id,
      userName: currentUser.name,
      isRead: false,
      createdAt: new Date()
    });
    
    setComments(updatedComments);
    setNewComment('');
  };

  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">הערות</h3>
      
      <div className="mb-6">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="הוסף הערה..."
          rows="3"
        />
        <button
          onClick={handleAddComment}
          disabled={!newComment.trim()}
          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
        >
          הוסף הערה
        </button>
      </div>
      
      <div className="space-y-4">
        {comments.length === 0 ? (
          <p className="text-gray-500">אין הערות עדיין</p>
        ) : (
          comments.map(comment => (
            <div key={comment.id} className="border-b pb-4">
              <p className="text-gray-800">{comment.text}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm font-medium text-gray-600">
                  {comment.authorName}
                </span>
                <span className="text-sm text-gray-400">•</span>
                <span className="text-sm text-gray-500">
                  {new Date(comment.createdAt).toLocaleDateString('he-IL')}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Task Activity Component
const TaskActivity = ({ task }) => {
  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">פעילות</h3>
      <p className="text-gray-600">אין פעילות עדיין</p>
    </div>
  );
};

// Task Files Component
const TaskFiles = ({ task }) => {
  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">קבצים</h3>
      <p className="text-gray-600">אין קבצים עדיין</p>
    </div>
  );
};

export default Tasks;